<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Methodology" tab (second tab).

    WHAT: The metric's METHODOLOGY — what it computes — in the original
      FORMATTED read layout (summary paragraph, labeled bullet sections, amber
      caveats, data-sources aside), with CLICK-TO-EDIT: click any field to get
      an inline editor; blur returns to the formatted view. Auto-generate
      writes ONLY the methodology (mode 'methodology': from the composed SQL
      when built, else as a buildable commitment). The expectation lives on
      its own first tab.

    CONTRACT (props { record }):
      READS  record.composable_config.methodology (+ contract/ for staleness
             and in-flight job resume)
      EMITS  patch({ composable_config: { ...config, methodology } }) —
             clone-then-overlay; unknown keys survive.
      POST   generate-contract/ { mode: 'methodology' } → poll → refresh().

    NOTES:
      - STALE = the step DA SQL changed since the methodology was written.
      - data_sources are BARE SCHEMA.TABLE chips (add on Enter, × to remove).
      - selfChrome: true — draws its own cards.
  -->
  <div class="space-y-3">
    <div class="flex items-start gap-3">
      <div class="flex-1 min-w-0">
        <div class="text-sm font-semibold text-gray-800">Methodology</div>
        <div class="text-xs text-gray-400 mt-0.5">
          What this metric computes. Click any field to edit it; auto-generate rewrites the
          whole document (from the SQL when built, as a buildable commitment when not).
        </div>
      </div>
      <button
        class="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-2 hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
        :style="{ background: '#2563EB' }"
        :disabled="generating"
        @click="generate"
      >
        <font-awesome-icon
          :icon="['fas', generating ? 'circle-notch' : 'wand-magic-sparkles']"
          :class="generating ? 'fa-spin' : ''"
          class="text-[10px]"
        />
        {{ generating ? 'Generating…' : (hasMethodology ? 'Auto-generate (replace)' : 'Auto-generate') }}
      </button>
    </div>

    <div
      v-if="isStale && hasMethodology"
      class="flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg border text-xs leading-relaxed bg-amber-50 border-amber-200 text-amber-800"
    >
      <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mt-0.5 flex-shrink-0" />
      <span>
        This metric's data sources have changed since the methodology was written, so it
        may describe old logic. Auto-generate to bring it up to date, or edit it directly.
      </span>
    </div>

    <div v-if="msg" class="text-xs rounded-lg px-3 py-2 border"
         :class="err ? 'text-red-600 bg-red-50 border-red-200' : 'text-gray-600 bg-gray-50 border-gray-200'">
      {{ msg }}
    </div>

    <div v-if="!hasMethodology && !generating"
         class="text-center text-xs text-gray-400 border border-dashed border-gray-200 rounded-xl py-10">
      No methodology yet. Auto-generate, or click any field below to write it by hand.
    </div>

    <div class="flex items-start gap-3">
      <div class="flex-1 min-w-0 bg-white border border-gray-200 rounded-xl">
        <!-- summary paragraph -->
        <div class="px-5 pt-4 pb-4">
          <textarea
            v-if="editing === 'summary'"
            ref="editor"
            v-model="form.summary"
            rows="2"
            class="w-full text-[13px] leading-relaxed border border-blue-300 rounded-lg px-3 py-2 focus:outline-none autogrow"
            @blur="editing = null"
          ></textarea>
          <p v-else class="text-[13px] leading-relaxed text-gray-800 editable" title="Click to edit" @click="edit('summary')">
            {{ form.summary || '— click to write a summary' }}
          </p>
        </div>

        <dl class="border-t border-gray-100">
          <div v-for="f in fields" :key="f.key" class="px-5 py-3.5 border-b border-gray-100">
            <dt class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              {{ f.label }}
            </dt>
            <dd v-if="editing === f.key">
              <textarea
                ref="editor"
                v-model="form[f.key]"
                :rows="f.single ? 1 : 3"
                class="w-full text-xs leading-relaxed border border-blue-300 rounded-lg px-3 py-2 focus:outline-none autogrow"
                :placeholder="f.single ? '' : 'one item per line'"
                @blur="editing = null"
              ></textarea>
            </dd>
            <dd v-else class="editable" title="Click to edit" @click="edit(f.key)">
              <span v-if="f.single" class="text-xs leading-relaxed text-gray-700 block">
                {{ form[f.key] || '—' }}
              </span>
              <ul v-else-if="asList(form[f.key]).length" class="space-y-1">
                <li v-for="(item, i) in asList(form[f.key])" :key="i"
                    class="text-xs leading-relaxed text-gray-700 flex gap-2">
                  <span class="text-gray-300 select-none flex-shrink-0">•</span>
                  <span>{{ item }}</span>
                </li>
              </ul>
              <span v-else class="text-xs text-gray-300">—</span>
            </dd>
          </div>

          <!-- caveats -->
          <div class="px-5 py-3.5 bg-amber-50/50 rounded-b-xl">
            <dt class="text-[11px] font-semibold text-amber-700 uppercase tracking-wide mb-1.5">
              Caveats
            </dt>
            <dd v-if="editing === 'caveats'">
              <textarea
                ref="editor"
                v-model="form.caveats"
                rows="3"
                class="w-full text-xs leading-relaxed border border-amber-300 rounded-lg px-3 py-2 focus:outline-none autogrow bg-white"
                placeholder="one caveat per line"
                @blur="editing = null"
              ></textarea>
            </dd>
            <dd v-else class="editable" title="Click to edit" @click="edit('caveats')">
              <ul v-if="asList(form.caveats).length" class="space-y-1.5">
                <li v-for="(item, i) in asList(form.caveats)" :key="i"
                    class="text-xs leading-relaxed text-amber-900 flex gap-2">
                  <span class="text-amber-400 select-none flex-shrink-0">•</span>
                  <span>{{ item }}</span>
                </li>
              </ul>
              <span v-else class="text-xs text-amber-300">—</span>
            </dd>
          </div>
        </dl>
      </div>

      <!-- sources: reference chips, editable in kind -->
      <aside class="w-80 flex-shrink-0 bg-white border border-gray-200 rounded-xl p-4">
        <div class="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2.5">
          Data Sources
        </div>
        <div class="space-y-1.5">
          <div
            v-for="(src, i) in form.data_sources"
            :key="src"
            class="rounded-md border border-sky-200 bg-sky-50 px-2.5 py-1.5 flex items-start justify-between gap-1.5"
          >
            <div class="min-w-0">
              <div v-if="srcSchema(src)" class="font-mono text-[11px] leading-tight text-sky-500">{{ srcSchema(src) }}</div>
              <div class="font-mono text-[11px] leading-tight font-semibold text-sky-800 break-all">{{ srcTable(src) }}</div>
            </div>
            <button class="text-sky-300 hover:text-red-500 leading-none flex-shrink-0" title="Remove" @click.prevent="removeSource(i)">×</button>
          </div>
          <input
            v-model="newSource"
            type="text"
            placeholder="＋ SCHEMA.TABLE — Enter to add"
            class="w-full text-[11px] font-mono px-2 py-1.5 border border-dashed border-gray-300 rounded-md bg-transparent focus:outline-none focus:border-blue-400"
            @keydown.enter.prevent="addSource"
          />
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const POLL_MS = 5000;
const MAX_POLLS = 120;

export default {
  name: 'MethodologySection',
  components: { FontAwesomeIcon },
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, default: () => ({}) },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch', 'refresh'],
  data() {
    return {
      form: this.hydrate(),
      hydrating: true,
      editing: null,
      isStale: false,
      generating: false,
      msg: '',
      err: false,
      newSource: '',
      pollCount: 0,
      // Ordered so it reads as an argument: what a row is, who's in scope,
      // what counts, when it's measured, then what's left out and which way
      // is good. single: one sentence, not a bullet list.
      fields: [
        { key: 'grain', label: 'Grain', single: true },
        { key: 'population', label: 'Population' },
        { key: 'denominator', label: 'Denominator' },
        { key: 'numerator', label: 'Numerator' },
        { key: 'timing', label: 'Timing' },
        { key: 'exclusions', label: 'Exclusions' },
        { key: 'direction', label: 'Direction', single: true },
      ],
    };
  },
  computed: {
    methodology() {
      return (this.record.composable_config || {}).methodology || null;
    },
    hasMethodology() { return !!this.methodology; },
  },
  watch: {
    // Same guards as ExpectationSection — see there.
    form: {
      deep: true,
      handler() {
        if (this._hydrationToken !== this._liveToken) return;
        this.emit();
      },
    },
    methodology() {
      if (this.generating || this.editing) return;
      this.rehydrate();
    },
  },
  created() {
    this._liveToken = 0;
    this._hydrationToken = 0;
    // What hydration put in the form. staleFields() compares against this to tell
    // a user's deliberate clear from a field that was never touched.
    this._baseline = { ...this.form, data_sources: [...(this.form.data_sources || [])] };
  },
  mounted() {
    this.loadMeta();
    this._hydrationToken = this._liveToken;
    this.hydrating = false;
  },
  beforeUnmount() { clearTimeout(this._pollTimer); },
  methods: {
    edit(key) {
      this.editing = key;
      this.$nextTick(() => {
        const el = Array.isArray(this.$refs.editor) ? this.$refs.editor[0] : this.$refs.editor;
        if (el) el.focus();
      });
    },
    lines(v) {
      if (Array.isArray(v)) return v.join('\n');
      return v ? String(v) : '';
    },
    unlines(s) {
      return String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);
    },
    asList(s) {
      return this.unlines(s);
    },
    srcSchema(src) {
      const i = String(src).lastIndexOf('.');
      return i > 0 ? String(src).slice(0, i + 1) : '';
    },
    srcTable(src) {
      const i = String(src).lastIndexOf('.');
      return i > 0 ? String(src).slice(i + 1) : String(src);
    },
    addSource() {
      const v = this.newSource.trim();
      if (!v) return;
      if (!this.form.data_sources.includes(v)) this.form.data_sources.push(v);
      this.newSource = '';
    },
    removeSource(i) {
      this.form.data_sources.splice(i, 1);
    },
    hydrate() {
      const m = (this.record.composable_config || {}).methodology || {};
      return {
        summary: m.summary || '',
        grain: Array.isArray(m.grain) ? m.grain.join(' ') : (m.grain || ''),
        direction: Array.isArray(m.direction) ? m.direction.join(' ') : (m.direction || ''),
        population: this.lines(m.population),
        denominator: this.lines(m.denominator),
        numerator: this.lines(m.numerator),
        timing: this.lines(m.timing),
        exclusions: this.lines(m.exclusions),
        data_sources: Array.isArray(m.data_sources) ? [...m.data_sources] : [],
        caveats: this.lines(m.caveats),
      };
    },
    // Refill from the record; invalidate emits queued against the old contents.
    rehydrate() {
      this._liveToken += 1;
      const token = this._liveToken;
      this.hydrating = true;
      this.form = this.hydrate();
      this._baseline = { ...this.form, data_sources: [...(this.form.data_sources || [])] };
      this.$nextTick(() => {
        if (this._liveToken !== token) return;
        this._hydrationToken = token;
        this.hydrating = false;
      });
    },
    emit() {
      if (this.hydrating) return;
      const f = this.form;
      const m = { ...(this.methodology || {}) };
      m.summary = f.summary || null;
      m.grain = f.grain || null;
      m.direction = f.direction || null;
      for (const key of ['population', 'denominator', 'numerator', 'timing',
                         'exclusions', 'caveats']) {
        m[key] = this.unlines(f[key]);
      }
      m.data_sources = [...f.data_sources];

      // STALE-FORM GUARD — see ExpectationSection.
      if (this.staleFields().length) {
        this.rehydrate();
        return;
      }

      this.$emit('patch', {
        composable_config: { ...(this.record.composable_config || {}), methodology: m },
      });
    },
    // Fields the record has, the form does not, AND the user never touched.
    // PROVENANCE, NOT CONTENT — see the long note in ExpectationSection.staleFields:
    // judging on emptiness alone makes clearing a field impossible, because a
    // deliberate delete and a stale ghost look identical.
    staleFields() {
      const prior = this.methodology;
      if (!prior) return [];
      const f = this.form;
      const base = this._baseline || {};
      const filled = (v) => String(v == null ? '' : v).trim().length > 0;
      const untouched = (k, formVal) => String(formVal ?? '') === String(base[k] ?? '');
      const out = [];
      for (const k of ['summary', 'grain', 'direction']) {
        if (typeof prior[k] === 'string' && prior[k].trim() && !filled(f[k])
            && untouched(k, f[k])) out.push(k);
      }
      for (const k of ['population', 'denominator', 'numerator', 'timing',
                       'exclusions', 'caveats']) {
        if (Array.isArray(prior[k]) && prior[k].length && !filled(f[k])
            && untouched(k, f[k])) out.push(k);
      }
      if (Array.isArray(prior.data_sources) && prior.data_sources.length
          && !(Array.isArray(f.data_sources) && f.data_sources.length)
          && (base.data_sources || []).length === 0) {
        out.push('data_sources');
      }
      return out;
    },

    async loadMeta() {
      try {
        const { data } = await axios.get(
          `api/impact_analytics/composable-metrics/${this.record.id}/contract/`);
        this.isStale = !!data.is_stale;
        const lj = data.latest_job;
        const ageMin = lj ? (Date.now() - new Date(lj.created_at).getTime()) / 60000 : Infinity;
        if (lj && ['QUEUED', 'PROCESSING'].includes(lj.status) && ageMin < 10) {
          this.generating = true;
          this.pollCount = 0;
          this.schedulePoll(lj.id);
        }
      } catch (e) { /* staleness is advisory — the view must still render */ }
    },

    async generate() {
      this.generating = true;
      this.err = false;
      this.msg = 'The definition agent is writing the methodology — a few minutes.';
      try {
        const { data } = await axios.post(
          `api/impact_analytics/composable-metrics/${this.record.id}/generate-contract/`,
          { mode: 'methodology' },
        );
        if (data.status === 'FAILED') throw new Error('The job failed to start.');
        this.pollCount = 0;
        this.schedulePoll(data.job_id);
      } catch (e) {
        this.generating = false;
        this.err = true;
        this.msg = `Generation failed: ${e?.response?.data?.error || e.message}`;
      }
    },
    schedulePoll(jobId) {
      clearTimeout(this._pollTimer);
      this._pollTimer = setTimeout(() => this.poll(jobId), POLL_MS);
    },
    async poll(jobId) {
      this.pollCount += 1;
      try {
        const { data: job } = await axios.get(`/api/personal-analyst/jobs/${jobId}/`);
        if (job.status === 'COMPLETED') {
          this.generating = false;
          this.msg = '';
          this.$emit('refresh');
          return;
        }
        if (job.status === 'FAILED') {
          this.generating = false;
          this.err = true;
          this.msg = `Generation failed: ${job.error_message || 'the worker did not complete.'}`;
          return;
        }
      } catch (e) { /* transient poll error — keep trying */ }
      if (this.pollCount >= MAX_POLLS) {
        this.generating = false;
        this.err = true;
        this.msg = 'Taking too long — check back later.';
        return;
      }
      this.schedulePoll(jobId);
    },
  },
};
</script>

<style scoped>
textarea.autogrow {
  field-sizing: content;
  min-height: 2.6rem;
  max-height: 60vh;
  resize: vertical;
  overflow-y: auto;
}
.editable {
  cursor: text;
  border-radius: 0.5rem;
  margin: -0.25rem;
  padding: 0.25rem;
}
.editable:hover {
  outline: 1px dashed #bfdbfe;
}
</style>
